import { useContext, useEffect, useState } from "react";

import { Spinner } from "@salesforce/design-system-react";

import { Button, Notification } from "@vimeo/iris/components";

import { AnimatedThumbnail, ListResponse, Video } from "vimeo";
import { getVideoIdFromUri, openCreateGifWidget, appOrigin } from "../common";
import EmbedInEmailContext, {
    EmbedInEmailContextState,
} from "../contexts/embedInEmailContext";
import VideoSettingsContext from "../contexts/videoSettingsContext";

import { client as vimeoClient } from "../vimeoClient";
import { EmbedInEmail } from "./EmbedInEmail/EmbedInEmail";

import copy from "copy-to-clipboard";

import { VerticalDivider } from "../components/VerticalDivider";
import { VimeoPlayer } from "./VimeoPlayer";
import { ConfirmModal } from "../components/ConfirmModal";

import "./settings.scoped.scss";
import { ThumbnailCard } from "../components/ThumbnailCard";
import {
    EmbedInEmailSource,
    EventName,
    getSfmcLocation,
    sendAnalyticsEvent,
} from "../analytics/common";

export function GifsSettings() {
    const [embedInEmailContextState, setEmbedInEmailContextState] = useState<
        Partial<EmbedInEmailContextState>
    >({});
    const [videoSettingsContext] = useContext(VideoSettingsContext);

    const [gifs, setGifs] = useState<AnimatedThumbnail[]>([]);
    const [loading, setLoading] = useState(false);
    const [showEmbedInEmailModal, setShowEmbedInEmailModal] = useState(false);
    const [video, setVideo] = useState<Video>();
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [gifToDelete, setGifToDelete] = useState<string>();
    const [showCopiedNotification, setShowCopiedNotification] = useState(false);

    useEffect(() => {
        setVideo(videoSettingsContext.video);
    }, [videoSettingsContext]);

    async function getAnimatedThumbnails() {
        const resp = await vimeoClient.get<ListResponse<AnimatedThumbnail>>(
            `/api/vimeo${video?.uri}/animated_thumbsets`
        );
        setGifs(resp.data.data);
    }

    useEffect(() => {
        if (!video) {
            return;
        }

        sendAnalyticsEvent(EventName.pageView, {
            name: "video_settings",
            location: getSfmcLocation(),
            video_id: getVideoIdFromUri(video.uri),
            section: "gifs",
        });

        setLoading(true);

        getAnimatedThumbnails()
            .catch(console.error)
            .finally(() => setLoading(false));

        setEmbedInEmailContextState({
            videoId: getVideoIdFromUri(video.uri),
            video: video,
            link: video.link,
            name: video.name,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video]);

    async function deleteGif(uri: string) {
        await vimeoClient.delete(`/api/vimeo${uri}`);
    }

    async function onVisibilityChange() {
        if (document.visibilityState !== "visible") {
            return;
        }

        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        setLoading(true);
        await getAnimatedThumbnails();
        setLoading(false);
    }

    function onDownloadOptionClicked(link: string) {
        window.open(link);
    }

    function onDeleteOptionClicked(uri: string) {
        setGifToDelete(uri);
        setShowDeleteConfirmModal(true);
    }

    async function onConfirmDelete() {
        if (!gifToDelete) {
            return;
        }

        setShowDeleteConfirmModal(false);
        await deleteGif(gifToDelete);
        setGifToDelete(undefined);

        // Refresh the GIFs.
        setLoading(true);
        await getAnimatedThumbnails();
        setLoading(false);
    }

    function onEmbedInEmailClicked(gifUri: string) {
        const gif = gifs.filter((t) => t.uri === gifUri)[0];
        setEmbedInEmailContextState({
            ...embedInEmailContextState,
            // The item in the second index position seems to always
            // have the link_with_play_button populated. So let's
            // use that. This is especially the case with GIFs.
            imgUrl: gif.sizes[1].link,
            imgUrlWithPlayButton: gif.sizes[1].link_with_play_button,
            selectedImgType: "gif",
            source: EmbedInEmailSource.videoSettings,
        });

        setShowEmbedInEmailModal(true);

        sendAnalyticsEvent(EventName.openEmbedInEmail, {
            path: null,
            is_my_videos: null,
            clip_id: embedInEmailContextState.videoId,
            location: "video_settings",
        }, 3);
    }

    async function onCreateGifClicked() {
        const videoId = embedInEmailContextState.videoId;

        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "open_gif_modal",
            location: "video_settings",
            video_id: videoId,
            cta: "create_gif",
        });

        const opened = await openCreateGifWidget(videoId);

        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        if (!opened) {
            return;
        }

        opened.onmessage = async (e: MessageEvent) => {
            if (e.origin !== appOrigin) {
                return;
            }

            await getAnimatedThumbnails();
        };
    }

    return (
        <div>
            {loading && <Spinner />}
            <div className="slds-grid">
                <div
                    className="slds-col"
                    style={{ display: "flex", flexFlow: "column nowrap" }}
                >
                    <div className="slds-var-p-bottom_large">
                        {!videoSettingsContext.hideSettingsViewHeading && (
                            <div className="slds-text-heading_medium">GIFs</div>
                        )}
                    </div>

                    {gifs.map((t, index) => (
                        <ThumbnailCard
                            key={index}
                            baseLink={t.sizes[1].link}
                            imgLink={t.sizes[1].link}
                            thumbnailType="gif"
                            thumbnailUri={t.uri}
                            createdOn={t.created_on}
                            duration={t.sizes[0].duration}
                            onCopyOptionClicked={(baseLink) => {
                                copy(baseLink);
                                setShowCopiedNotification(true);
                            }}
                            onDeleteOptionClicked={onDeleteOptionClicked}
                            onDownloadOptionClicked={onDownloadOptionClicked}
                            onEmbedInEmailClicked={onEmbedInEmailClicked}
                        />
                    ))}

                    {!loading && !gifs?.length && (
                        <div>You have no GIFs created.</div>
                    )}
                    <Button
                        format="primary"
                        className="slds-var-m-top_small"
                        onClick={onCreateGifClicked}
                    >
                        Create GIF
                    </Button>
                </div>
                <div>
                    <VerticalDivider />
                </div>
                <div className="slds-col">
                    {video && <VimeoPlayer video={video} />}
                </div>
            </div>
            {embedInEmailContextState.video && (
                <EmbedInEmailContext.Provider
                    value={[
                        embedInEmailContextState,
                        setEmbedInEmailContextState,
                    ]}
                >
                    <EmbedInEmail
                        source={EmbedInEmailSource.videoSettings}
                        useModal={true}
                        show={showEmbedInEmailModal}
                        onClose={() => setShowEmbedInEmailModal(false)}
                        hideAssetSelection={true}
                    />
                </EmbedInEmailContext.Provider>
            )}

            <ConfirmModal
                active={showDeleteConfirmModal}
                onClose={() => setShowDeleteConfirmModal(false)}
                message="Deleting a GIF cannot be undone. Are you sure?"
                onCancelClicked={() => setShowDeleteConfirmModal(false)}
                onConfirmClicked={onConfirmDelete}
            />

            <Notification
                content="Copied!"
                showing={showCopiedNotification}
                automatic={true}
                onComplete={() => setShowCopiedNotification(false)}
            ></Notification>
        </div>
    );
}
